import React, { useEffect, useRef, useState } from "react";
import { api } from "../../api";
import {
  X,
  Store,
  User,
  Phone,
  MapPin,
  Link2,
  Clock,
  FileText,
  BadgePercent,
  FileBadge,
  Save,
  Upload,
} from "lucide-react";
import "./VendorFormModal.css";

const INDIAN_PHONE_REGEX = /^(\+91[\-\s]?)?[6789]\d{9}$/;

const CATEGORIES = ["ISF Shop", "Medicines", "Consumables", "Infra", "Other"];
const WEEK_DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

// Open / close time choices every 30 minutes, stored as "HH:mm" and shown as
// "09:00 AM".
const formatTime = (value) => {
  const [hours, minutes] = value.split(":").map(Number);
  const suffix = hours < 12 ? "AM" : "PM";
  const hour12 = hours % 12 || 12;
  return `${String(hour12).padStart(2, "0")}:${String(minutes).padStart(2, "0")} ${suffix}`;
};

const TIME_OPTIONS = Array.from({ length: 48 }, (_, index) => {
  const hours = String(Math.floor(index / 2)).padStart(2, "0");
  return `${hours}:${index % 2 ? "30" : "00"}`;
});

// A saved time that isn't on the half hour still shows in its dropdown.
const timeOptionsWith = (value) =>
  value && !TIME_OPTIONS.includes(value) ? [...TIME_OPTIONS, value].sort() : TIME_OPTIONS;

// Shop icon: same limits as the product image upload (POST /api/v2/upload/image).
const ICON_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
const ICON_MAX_SIZE = 5 * 1024 * 1024;

const EMPTY_FORM = {
  shopIcon: "",
  name: "",
  vendorName: "",
  phone: "",
  address: "",
  googleMapLink: "",
  categories: [],
  products: "",
  openTime: "09:00",
  closeTime: "18:00",
  workingDays: [],
  openAllDays: false,
  minimumOrderValue: "",
  deliveryLeadTime: "",
  paymentTerms: "",
  keyPoints: "",
  discountOffer: "",
  preferredVendor: false,
  gstTaxDetails: "",
  active: true,
};

// readOnly: the Vendors list's View (eye) button - every field is shown but
// locked, with only a Close button.
export default function VendorFormModal({ vendor, onClose, onSubmit, readOnly = false }) {
  const isEditing = Boolean(vendor?._id);

  const [formData, setFormData] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [uploadingIcon, setUploadingIcon] = useState(false);
  const iconInputRef = useRef(null);

  useEffect(() => {
    if (!vendor) return;
    setFormData({
      ...EMPTY_FORM,
      ...Object.fromEntries(
        Object.keys(EMPTY_FORM)
          .filter((key) => vendor[key] !== undefined && vendor[key] !== null)
          .map((key) => [key, vendor[key]])
      ),
      minimumOrderValue: vendor.minimumOrderValue ?? "",
      active: vendor.active !== false,
    });
  }, [vendor]);

  const clearError = (name) => {
    if (!errors[name]) return;
    setErrors((prev) => {
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const setField = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    clearError(name);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setField(name, type === "checkbox" ? checked : value);
  };

  const toggleInList = (name, item) => {
    setFormData((prev) => ({
      ...prev,
      [name]: prev[name].includes(item)
        ? prev[name].filter((entry) => entry !== item)
        : [...prev[name], item],
    }));
    clearError(name);
  };

  const handleIconChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    if (!ICON_TYPES.includes(file.type)) {
      setErrors((prev) => ({ ...prev, shopIcon: "Use a JPEG, PNG or WebP image" }));
      return;
    }
    if (file.size > ICON_MAX_SIZE) {
      setErrors((prev) => ({ ...prev, shopIcon: "Image is too large (max 5 MB)" }));
      return;
    }

    setUploadingIcon(true);
    clearError("shopIcon");
    try {
      const body = new FormData();
      body.append("image", file);
      const response = await api.post("/api/v2/upload/image", body, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (!response.data?.success || !response.data?.url) {
        throw new Error(response.data?.message || "Upload failed");
      }
      setField("shopIcon", response.data.url);
    } catch (err) {
      setErrors((prev) => ({
        ...prev,
        shopIcon: err.response?.data?.message || err.message || "Icon upload failed",
      }));
    } finally {
      setUploadingIcon(false);
    }
  };

  const validate = () => {
    const nextErrors = {};

    if (!formData.name.trim()) nextErrors.name = "Shop name is required";
    if (!formData.vendorName.trim()) nextErrors.vendorName = "Vendor name is required";
    if (!formData.phone.trim()) {
      nextErrors.phone = "Contact number is required";
    } else if (!INDIAN_PHONE_REGEX.test(formData.phone.trim())) {
      nextErrors.phone = "Enter a valid Indian mobile number (e.g. 9876543210 or +91-9876543210)";
    }
    if (!formData.address.trim()) nextErrors.address = "Shop address is required";
    if (!formData.openTime) nextErrors.openTime = "Open time is required";
    if (!formData.closeTime) nextErrors.closeTime = "Close time is required";
    if (!formData.openAllDays && !formData.workingDays.length) {
      nextErrors.workingDays = "Select at least one working day";
    }
    if (!formData.categories.length) nextErrors.categories = "Select at least one category";
    if (!formData.products.trim()) nextErrors.products = "Products / subcategories are required";

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    setSubmitting(true);
    try {
      await onSubmit({
        shopIcon: formData.shopIcon,
        name: formData.name.trim(),
        vendorName: formData.vendorName.trim(),
        phone: formData.phone.trim(),
        address: formData.address.trim(),
        googleMapLink: formData.googleMapLink.trim(),
        categories: formData.categories,
        products: formData.products.trim(),
        openTime: formData.openTime,
        closeTime: formData.closeTime,
        workingDays: formData.openAllDays ? WEEK_DAYS : formData.workingDays,
        openAllDays: formData.openAllDays,
        minimumOrderValue:
          formData.minimumOrderValue === "" ? undefined : Number(formData.minimumOrderValue),
        deliveryLeadTime: formData.deliveryLeadTime.trim(),
        paymentTerms: formData.paymentTerms.trim(),
        keyPoints: formData.keyPoints.trim(),
        discountOffer: formData.discountOffer.trim(),
        preferredVendor: formData.preferredVendor,
        gstTaxDetails: formData.gstTaxDetails.trim(),
        active: Boolean(formData.active),
      });
    } finally {
      setSubmitting(false);
    }
  };

  const errorOf = (name) => errors[name] && <p className="vfm-error">{errors[name]}</p>;
  const inputClass = (name) => (errors[name] ? "vfm-invalid" : "");
  const required = <span className="vfm-required">*</span>;

  return (
    <div className="vfm-overlay" onClick={onClose}>
      <div
        className="vfm-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="vfm-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="vfm-header">
          <span className="vfm-header-icon" aria-hidden="true">
            <Store size={24} />
          </span>
          <div className="vfm-header-text">
            <h2 id="vfm-title">{readOnly ? "Vendor Details" : isEditing ? "Edit Vendor" : "Add Vendor"}</h2>
            <p>
              {readOnly
                ? vendor?.name
                : isEditing
                  ? "Update this vendor's details."
                  : "Add vendor details for future purchases."}
            </p>
          </div>
          <button
            type="button"
            className="vfm-close"
            onClick={onClose}
            disabled={submitting}
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="vfm-form" noValidate>
          {/* The scroll container is a div: a fieldset won't shrink and scroll
              as a flex item in Chrome. The fieldset inside only locks the fields. */}
          <div className="vfm-body">
          <fieldset className="vfm-fields" disabled={readOnly}>
            <h3 className="vfm-section-title">Basic information</h3>
            <div className="vfm-icon-upload">
              <span className="vfm-icon-preview">
                {formData.shopIcon ? (
                  <img src={formData.shopIcon} alt="Shop icon" />
                ) : (
                  <Store size={26} aria-hidden="true" />
                )}
              </span>
              <div className="vfm-icon-upload-text">
                <span className="vfm-label">Shop icon</span>
                {!readOnly && (
                  <div className="vfm-icon-upload-actions">
                    <button
                      type="button"
                      className="vfm-btn-upload"
                      onClick={() => iconInputRef.current?.click()}
                      disabled={submitting || uploadingIcon}
                    >
                      <Upload size={14} aria-hidden="true" />
                      {uploadingIcon ? "Uploading..." : formData.shopIcon ? "Change icon" : "Upload icon"}
                    </button>
                    {formData.shopIcon && (
                      <button
                        type="button"
                        className="vfm-btn-link"
                        onClick={() => setField("shopIcon", "")}
                        disabled={submitting || uploadingIcon}
                      >
                        Remove
                      </button>
                    )}
                    <span className="vfm-hint">JPEG, PNG or WebP, up to 5 MB (optional)</span>
                  </div>
                )}
                {errorOf("shopIcon")}
              </div>
              <input
                ref={iconInputRef}
                type="file"
                accept={ICON_TYPES.join(",")}
                onChange={handleIconChange}
                hidden
              />
            </div>
            <div className="vfm-grid vfm-grid-3">
              <div className="vfm-field">
                <label htmlFor="vfm-name">Shop name {required}</label>
                <div className="vfm-with-icon">
                  <Store size={18} className="vfm-icon" aria-hidden="true" />
                  <input
                    id="vfm-name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={inputClass("name")}
                    placeholder="Enter shop name"
                    disabled={submitting}
                  />
                </div>
                {errorOf("name")}
              </div>

              <div className="vfm-field">
                <label htmlFor="vfm-vendor-name">Vendor name {required}</label>
                <div className="vfm-with-icon">
                  <User size={18} className="vfm-icon" aria-hidden="true" />
                  <input
                    id="vfm-vendor-name"
                    name="vendorName"
                    value={formData.vendorName}
                    onChange={handleChange}
                    className={inputClass("vendorName")}
                    placeholder="Enter vendor name"
                    disabled={submitting}
                  />
                </div>
                {errorOf("vendorName")}
              </div>

              <div className="vfm-field">
                <label htmlFor="vfm-phone">Contact number {required}</label>
                <div className="vfm-with-icon">
                  <Phone size={18} className="vfm-icon" aria-hidden="true" />
                  <input
                    id="vfm-phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    className={inputClass("phone")}
                    placeholder="Enter mobile number"
                    disabled={submitting}
                  />
                </div>
                {errorOf("phone")}
              </div>
            </div>

            <h3 className="vfm-section-title">Shop details</h3>
            <div className="vfm-grid vfm-grid-address">
              <div className="vfm-field">
                <label htmlFor="vfm-address">Shop address {required}</label>
                <div className="vfm-with-icon vfm-with-icon-top">
                  <MapPin size={18} className="vfm-icon" aria-hidden="true" />
                  <textarea
                    id="vfm-address"
                    name="address"
                    rows={2}
                    value={formData.address}
                    onChange={handleChange}
                    className={inputClass("address")}
                    placeholder="Enter full address"
                    disabled={submitting}
                  />
                </div>
                {errorOf("address")}
              </div>

              <div className="vfm-field">
                <label htmlFor="vfm-map">Google Map link</label>
                <div className="vfm-with-icon">
                  <Link2 size={18} className="vfm-icon" aria-hidden="true" />
                  <input
                    id="vfm-map"
                    name="googleMapLink"
                    type="url"
                    value={formData.googleMapLink}
                    onChange={handleChange}
                    placeholder="Paste Google Map link (optional)"
                    disabled={submitting}
                  />
                </div>
              </div>
            </div>

            <h3 className="vfm-section-title">Shop timing</h3>
            <div className="vfm-grid vfm-grid-timing">
              <div>
                <label className="vfm-check">
                  <input
                    type="checkbox"
                    name="openAllDays"
                    checked={formData.openAllDays}
                    onChange={handleChange}
                    disabled={submitting}
                  />
                  Open all days (same timing)
                </label>
                <div className="vfm-times">
                  <div className="vfm-time">
                    <label htmlFor="vfm-open">Open time {required}</label>
                    <Clock size={18} className="vfm-icon" aria-hidden="true" />
                    <select
                      id="vfm-open"
                      name="openTime"
                      value={formData.openTime}
                      onChange={handleChange}
                      className={inputClass("openTime")}
                      disabled={submitting}
                    >
                      {timeOptionsWith(formData.openTime).map((time) => (
                        <option key={time} value={time}>{formatTime(time)}</option>
                      ))}
                    </select>
                  </div>
                  <div className="vfm-time">
                    <label htmlFor="vfm-close">Close time {required}</label>
                    <Clock size={18} className="vfm-icon" aria-hidden="true" />
                    <select
                      id="vfm-close"
                      name="closeTime"
                      value={formData.closeTime}
                      onChange={handleChange}
                      className={inputClass("closeTime")}
                      disabled={submitting}
                    >
                      {timeOptionsWith(formData.closeTime).map((time) => (
                        <option key={time} value={time}>{formatTime(time)}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <fieldset className="vfm-field vfm-fieldset">
                <legend>Working days {!formData.openAllDays && required}</legend>
                <div className="vfm-days">
                  {WEEK_DAYS.map((day) => (
                    <label key={day} className="vfm-check">
                      <input
                        type="checkbox"
                        checked={formData.openAllDays || formData.workingDays.includes(day)}
                        onChange={() => toggleInList("workingDays", day)}
                        disabled={submitting || formData.openAllDays}
                      />
                      {day}
                    </label>
                  ))}
                </div>
                {errorOf("workingDays")}
              </fieldset>
            </div>

            <h3 className="vfm-section-title">Product &amp; category details</h3>
            <fieldset className="vfm-field vfm-fieldset">
              <legend>Categories supplied {required}</legend>
              <div className="vfm-categories">
                {CATEGORIES.map((category) => (
                  <label key={category} className="vfm-check">
                    <input
                      type="checkbox"
                      checked={formData.categories.includes(category)}
                      onChange={() => toggleInList("categories", category)}
                      disabled={submitting}
                    />
                    {category}
                  </label>
                ))}
              </div>
              {errorOf("categories")}
            </fieldset>
            <div className="vfm-field">
              <label htmlFor="vfm-products">Products / Subcategories supplied {required}</label>
              <input
                id="vfm-products"
                name="products"
                value={formData.products}
                onChange={handleChange}
                className={inputClass("products")}
                placeholder="Enter products or subcategories (e.g. notebooks, tablets, biscuits, etc.)"
                disabled={submitting}
              />
              {errorOf("products")}
            </div>

            <h3 className="vfm-section-title">Service &amp; operational details</h3>
            <div className="vfm-grid vfm-grid-3">
              <div className="vfm-field">
                <label htmlFor="vfm-min-order">Minimum order value (₹)</label>
                <div className="vfm-prefixed">
                  <span aria-hidden="true">₹</span>
                  <input
                    id="vfm-min-order"
                    name="minimumOrderValue"
                    type="number"
                    min="0"
                    value={formData.minimumOrderValue}
                    onChange={handleChange}
                    placeholder="Enter amount"
                    disabled={submitting}
                  />
                </div>
              </div>

              <div className="vfm-field">
                <label htmlFor="vfm-lead-time">Delivery lead time</label>
                <input
                  id="vfm-lead-time"
                  name="deliveryLeadTime"
                  value={formData.deliveryLeadTime}
                  onChange={handleChange}
                  placeholder="e.g. 2–3 days"
                  disabled={submitting}
                />
              </div>

              <div className="vfm-field">
                <label htmlFor="vfm-payment">Payment terms</label>
                <input
                  id="vfm-payment"
                  name="paymentTerms"
                  value={formData.paymentTerms}
                  onChange={handleChange}
                  placeholder="e.g. Advance, COD, 30 days"
                  disabled={submitting}
                />
              </div>
            </div>

            <h3 className="vfm-section-title">Additional information</h3>
            <div className="vfm-grid vfm-grid-address">
              <div className="vfm-field">
                <label htmlFor="vfm-key-points">Key points / Feedback</label>
                <div className="vfm-with-icon vfm-with-icon-top">
                  <FileText size={20} className="vfm-icon" aria-hidden="true" />
                  <textarea
                    id="vfm-key-points"
                    name="keyPoints"
                    rows={2}
                    value={formData.keyPoints}
                    onChange={handleChange}
                    placeholder="Add notes or feedback (e.g. speciality items, quality, delivery instructions)"
                    disabled={submitting}
                  />
                </div>
              </div>

              <div className="vfm-field">
                <label htmlFor="vfm-discount">Discount offer</label>
                <div className="vfm-with-icon">
                  <BadgePercent size={20} className="vfm-icon" aria-hidden="true" />
                  <input
                    id="vfm-discount"
                    name="discountOffer"
                    value={formData.discountOffer}
                    onChange={handleChange}
                    placeholder="Enter offer, if any"
                    disabled={submitting}
                  />
                </div>
              </div>
            </div>

            <h3 className="vfm-section-title">Status &amp; preferences</h3>
            <div className="vfm-grid vfm-grid-status">
              <div className="vfm-field">
                <span className="vfm-label">Status</span>
                <label className="vfm-switch">
                  <input
                    type="checkbox"
                    name="active"
                    checked={formData.active}
                    onChange={handleChange}
                    disabled={submitting}
                  />
                  <span className="vfm-switch-track" aria-hidden="true" />
                  <strong>{formData.active ? "Active" : "Inactive"}</strong>
                  <span className="vfm-hint">Inactive vendors will not be shown while creating purchase requests.</span>
                </label>
              </div>

              <fieldset className="vfm-field vfm-fieldset">
                <legend>Preferred vendor</legend>
                <div className="vfm-radios">
                  <label className="vfm-check">
                    <input
                      type="radio"
                      name="preferredVendor"
                      checked={formData.preferredVendor}
                      onChange={() => setField("preferredVendor", true)}
                      disabled={submitting}
                    />
                    Yes
                  </label>
                  <label className="vfm-check">
                    <input
                      type="radio"
                      name="preferredVendor"
                      checked={!formData.preferredVendor}
                      onChange={() => setField("preferredVendor", false)}
                      disabled={submitting}
                    />
                    No
                  </label>
                </div>
              </fieldset>

              <div className="vfm-field">
                <label htmlFor="vfm-gst">GST / Tax details</label>
                <div className="vfm-with-icon">
                  <FileBadge size={20} className="vfm-icon" aria-hidden="true" />
                  <input
                    id="vfm-gst"
                    name="gstTaxDetails"
                    value={formData.gstTaxDetails}
                    onChange={handleChange}
                    placeholder="Enter GST / Tax details (optional)"
                    disabled={submitting}
                  />
                </div>
              </div>
            </div>
          </fieldset>
          </div>

          <div className="vfm-footer">
            <span className="vfm-footer-note">
              {!readOnly && (
                <>
                  <span className="vfm-required">*</span> Required fields
                </>
              )}
            </span>
            <div className="vfm-footer-actions">
              <button type="button" className="vfm-btn-cancel" onClick={onClose} disabled={submitting}>
                {readOnly ? "Close" : "Cancel"}
              </button>
              {!readOnly && (
                <button type="submit" className="vfm-btn-save" disabled={submitting || uploadingIcon}>
                  <Save size={16} aria-hidden="true" />
                  {submitting ? "Saving..." : isEditing ? "Save Changes" : "Save Vendor"}
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
